import React, { useEffect, useState } from 'react';
import { Box, Checkbox, FormControlLabel } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { OrganizationAddressStep } from '@gastrosoft/shared/ui';
import { getSetupData, saveSetupData } from '../utils/setupStorage';

interface CustomerContactStepProps {
  onNext: () => void;
  onBack: () => void;
}

interface AddressDraft {
  addressLine1: string;
  addressLine2: string;
  city: string;
  zipCode: string;
  country: string;
  email: string;
  phoneNumber: string;
}

const emptyAddress: AddressDraft = {
  addressLine1: '',
  addressLine2: '',
  city: '',
  zipCode: '',
  country: 'DE',
  email: '',
  phoneNumber: '',
};

/**
 * Wraps the shared `OrganizationAddressStep` for the customer-contact address
 * (used when inviting customers) with a "same as headquarters" checkbox that
 * prefills from step 2's draft. Toggling forces a remount (via `key`) so the
 * shared form re-reads its seeded localStorage draft.
 */
const CustomerContactStep: React.FC<CustomerContactStepProps> = ({ onNext, onBack }) => {
  const { t } = useTranslation();
  const [sameAsHeadquarters, setSameAsHeadquarters] = useState<boolean>(() => {
    const existing = getSetupData<Partial<AddressDraft>>('customerContact');
    return !existing;
  });

  // Prefill on first mount when defaulting to "same as headquarters". Subsequent
  // toggles are handled explicitly in handleToggle.
  useEffect(() => {
    if (sameAsHeadquarters) {
      const headquarters = getSetupData<Partial<AddressDraft>>('organizationAddress');
      saveSetupData('customerContact', { ...emptyAddress, ...headquarters });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggle = (checked: boolean) => {
    setSameAsHeadquarters(checked);
    if (checked) {
      const headquarters = getSetupData<Partial<AddressDraft>>('organizationAddress');
      saveSetupData('customerContact', { ...emptyAddress, ...headquarters });
    } else {
      saveSetupData('customerContact', { ...emptyAddress });
    }
  };

  return (
    <Box>
      <FormControlLabel
        control={
          <Checkbox checked={sameAsHeadquarters} onChange={(e) => handleToggle(e.target.checked)} />
        }
        label={t('setup.customerContact.sameAsHeadquarters')}
        sx={{ mb: 1 }}
      />
      <OrganizationAddressStep
        key={sameAsHeadquarters ? 'same' : 'custom'}
        onNext={onNext}
        onBack={onBack}
        localStorageKey="customerContact"
        titleKey="CustomerContactAddress"
        descriptionKey="SetupCustomerContactDescription"
      />
    </Box>
  );
};

export default CustomerContactStep;
